import { useEffect, useRef, useState } from 'react';

import moment from 'moment';

import { ColorSAP, FONT_FAMILY } from '../../ontego-ui';
import Button from '../Button/Button';
import Form from '../Form/Form';
import TertiaryButton from '../TertiaryButton/TertiaryButton';
import { Divider, Fab, Paper, Popover, styled, Typography } from '@mui/material';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import Styled from './CustomDateRangePickerStyles';

const CustomPaper = styled(Paper)(() => ({
    borderRadius: 8,
    boxShadow: '0px 2px 29px 0px rgba(0, 0, 0, 0.12)',
    zIndex: 9999999
}));

const GLOBAL_BOX_SIZE = 30
const GLOBAL_FONT_SIZE = 13
const GLOBAL_PADDING = '10px 0px'

const DAYS_HEADER = [
    { dayName: 'Sunday', shorthand: 'S' },
    { dayName: 'Monday', shorthand: 'M' },
    { dayName: 'Tuesday', shorthand: 'D' },
    { dayName: 'Wednesday', shorthand: 'M' },
    { dayName: 'Thursday', shorthand: 'D' },
    { dayName: 'Friday', shorthand: 'F' },
    { dayName: 'Saturday', shorthand: 'S' }
]

const formatDoubleDigitDate = date => {
    if (date.length === 1) {
        return `0${date}`
    }
    else if (date.length === 2) {
        return date
    }
}

const calculateDifferenceFromStart = day => {
    let diff = 0

    switch (day) {
        case 'Sunday':
            diff = 0
            break
        case 'Monday':
            diff = 1
            break
        case 'Tuesday':
            diff = 2
            break
        case 'Wednesday':
            diff = 3
            break
        case 'Thursday':
            diff = 4
            break
        case 'Friday':
            diff = 5
            break
        case 'Saturday':
            diff = 6
            break
    }

    return diff
}

const calculateDifferenceFromEnd = day => {
    let diff = 0

    switch (day) {
        case 'Sunday':
            diff = 6
            break
        case 'Monday':
            diff = 5
            break
        case 'Tuesday':
            diff = 4
            break
        case 'Wednesday':
            diff = 3
            break
        case 'Thursday':
            diff = 2
            break
        case 'Friday':
            diff = 1
            break
        case 'Saturday':
            diff = 0
            break
    }

    return diff
}

const MonthTitle = props => {
    const { children } = props

    return (
        <div style={{ paddingTop: 15, paddingBottom: 20 }}>
            <Typography
                variant="h1"
                style={{ fontFamily: FONT_FAMILY.PLUS_JAKARTA_SANS_BOLD, fontSize: 16, color: ColorSAP.neutral[100] }}
            >
                <b>{children}</b>
            </Typography>
        </div>
    )
}

const SelectorBackground = props => {
    const { children, color } = props
    return (
        <>
            <Typography
                variant="h1"
                style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: GLOBAL_FONT_SIZE,
                    color: color
                }}
            >
                {children}
            </Typography>
        </>
    )
}

const DateText = props => {
    const { children, color } = props
    return (
        <div style={{ width: GLOBAL_BOX_SIZE, height: GLOBAL_BOX_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
                variant="h1"
                style={{
                    fontWeight: 400,
                    fontFamily: "Inter, sans-serif",
                    fontSize: GLOBAL_FONT_SIZE,
                    color: color
                }}
            >
                {children}
            </Typography>
        </div>
    )
}

const TodayIndicator = props => {
    const { color } = props
    return (
        <div style={{ position: 'absolute', bottom: 4, left: 14, width: 4, height: 4, backgroundColor: color, borderRadius: '50%', zIndex: 100 }} />
    )
}

const DateLists = props => {
    const { data, dateStart, dateEnd, onDateClick } = props

    return (
        <>
            {data.days.map((day, index) => {
                const isDisabled = []

                if (dateStart && !dateEnd) {
                    isDisabled.push(moment(day.value).isBefore(moment(dateStart)))
                }

                const isToday = day.value === moment().format('YYYY-MM-DD')

                const isCheckIn = moment(day.value).isSame(moment(dateStart))
                const isCheckOut = moment(day.value).isSame(moment(dateEnd))
                const isBetween = moment(day.value).isAfter(moment(dateStart)) && moment(day.value).isBefore(moment(dateEnd))

                const dateText = day.date ? (day.date[0] === '0' ? day.date[1] : day.date) : ``

                return (
                    <Styled.DateGridItem
                        key={`${index}-${day.value}`}
                        style={{
                            cursor: isDisabled.includes(true) ? 'not-allowed' : 'pointer',
                            // opacity: isDisabled.includes(true) ? 0.3 : 1,
                            userSelect: isDisabled.includes(true) ? 'none' : 'auto',
                        }}
                        onClick={!isDisabled.includes(true) ? onDateClick(day.value) : undefined}
                        isCheckIn={isCheckIn}
                        isCheckOut={isCheckOut}
                        isBetween={isBetween}
                        isDateEnd={!!dateEnd}
                    >
                        {isCheckIn && (
                            <Styled.DateGridItemSelectorStartBackground>
                                <SelectorBackground color={`#FFF`}>
                                    {dateText}
                                </SelectorBackground>
                            </Styled.DateGridItemSelectorStartBackground>
                        )}
                        {isCheckOut && (
                            <Styled.DateGridItemSelectorEndBackground>
                                <SelectorBackground color={ColorSAP.primary[70]}>
                                    {dateText}
                                </SelectorBackground>
                            </Styled.DateGridItemSelectorEndBackground>
                        )}
                        <DateText
                            color={isDisabled.includes(true) ? ColorSAP.neutral[30] : (isToday ? ColorSAP.primary[100] : (isCheckIn ? '#FFF' : ColorSAP.neutral[100]))}
                        >
                            {dateText}
                        </DateText>
                        {isToday && (
                            <TodayIndicator
                                color={isCheckIn ? '#FFF' : ColorSAP.primary[100]}
                            />
                        )}
                    </Styled.DateGridItem>
                )
            })}
        </>
    )
}

const DesktopDatepicker = props => {
    const { datepickerData, dateStart, dateEnd, min = moment(), max = null, onDateClick, handleChangeNextMonth, handleChangePreviousMonth, onReset, onClose } = props

    const scrollRef = useRef(null)

    const handleNavigateNextIndex = () => {
        handleChangeNextMonth()
    }

    const handleNavigateBeforeIndex = () => {
        handleChangePreviousMonth()
    }

    return (
        <div style={{ position: 'relative', backgroundColor: '#FFF' }}>
            <div
                ref={scrollRef}
                id="datepicker-desktop-container"
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: 'column',
                    width: 560,
                    height: 340,
                    overflowX: 'scroll',
                    scrollBehavior: 'smooth',
                    scrollSnapType: 'x mandatory',
                    msScrollSnapPointsX: 'repeat(280px)'
                }}
            >
                {datepickerData && datepickerData.map(data => {
                    return (
                        <div
                            key={`${data.month}-${data.year}`}
                            style={{ float: 'left', width: 280, textAlign: 'center', textAlign: '-webkit-center', boxSizing: 'border-box', padding: GLOBAL_PADDING, scrollSnapAlign: 'start' }}
                        >
                            <MonthTitle>
                                {`${data.monthLabel} ${data.year}`}
                            </MonthTitle>
                            <Styled.DateGrid>
                                {DAYS_HEADER.map((dayObj, index) => (
                                    <div key={index + "-" + dayObj.shorthand}>
                                        <div style={{ width: GLOBAL_BOX_SIZE, height: GLOBAL_BOX_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography
                                                variant="h1"
                                                style={{ fontFamily: FONT_FAMILY.PLUS_JAKARTA_SANS_MEDIUM, fontSize: GLOBAL_FONT_SIZE, color: ColorSAP.neutral[100] }}
                                            >
                                                {dayObj.shorthand}
                                            </Typography>
                                        </div>
                                    </div>
                                ))}
                                <DateLists
                                    data={data}
                                    dateStart={dateStart}
                                    dateEnd={dateEnd}
                                    onDateClick={onDateClick}
                                    min={min}
                                    max={max}
                                />
                            </Styled.DateGrid>
                        </div>
                    )
                })}
            </div>
            <Divider />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', boxSizing: 'border-box', padding: 12 }}>
                <div style={{ marginRight: '24px' }}>
                    <TertiaryButton
                        theme="light"
                        size="small"
                        onClick={onReset}
                        noIcon
                    >
                        {`Reset`}
                    </TertiaryButton>
                </div>
                <Button
                    type="primary"
                    theme="dark"
                    size="small"
                    onClick={onClose}
                >
                    {`Submit`}
                </Button>
            </div>

            <Styled.NavigationBefore onClick={handleNavigateBeforeIndex}>
                <Fab size="small" style={{ backgroundColor: ColorSAP.neutral[0], boxShadow: 'none' }}>
                    <NavigateBefore style={{ color: ColorSAP.neutral[90] }} />
                </Fab>
            </Styled.NavigationBefore>


            <Styled.NavigationNext onClick={handleNavigateNextIndex}
            >
                <Fab size="small" style={{ backgroundColor: ColorSAP.neutral[0], boxShadow: 'none' }}>
                    <NavigateNext style={{ color: ColorSAP.neutral[90] }} />
                </Fab>
            </Styled.NavigationNext>

        </div>
    )
}

const CustomDateRangePicker = props => {
    const { labelStart = 'Check in', labelEnd = 'Check out', placeholderStart, placeholderEnd, start, end, min = moment(), max = null, onChangeDate = ({ startDate, endDate }) => { console.log(`${startDate} - ${endDate}`) }, arrival, departure } = props

    const [anchorEl, setAnchorEl] = useState(null)

    const [dateStart, setDateStart] = useState(moment())
    const [dateEnd, setDateEnd] = useState(moment().add(1, 'days'))

    const [tempDateStart, setTempDateStart] = useState(moment())
    const [tempDateEnd, setTempDateEnd] = useState(moment().add(1, 'days'))

    const [datepickerData, setDatepickerData] = useState(null)

    const [selectedMonth, setSelectedMonth] = useState(moment())

    const [turn, setTurn] = useState(0)

    useEffect(() => {
        if (arrival) setDateStart(moment(arrival))
        if (departure) setDateEnd(moment(departure))
    }, [])

    const handleClickDateInput = e => {
        const anchor = document.getElementById('popover-anchor')
        if (anchor) {
            setAnchorEl(anchor)
        }
    }

    const handleClosePopover = () => {
        setDateStart(tempDateStart)
        setDateEnd(tempDateEnd)
        setTurn(0)
        setAnchorEl(null);
    }
    const handleSubmitPopover = () => {
        const start = dateStart ? moment(dateStart) : moment();
        const end = dateEnd ? (start.isSame(dateEnd, 'day') ? moment(dateEnd).add(1, 'days') : moment(dateEnd)) : start.clone().add(1, 'days');

        setDateStart(start)
        setDateEnd(end)

        setTempDateStart(start)
        setTempDateEnd(end)
        setTurn(0)

        onChangeDate({ startDate: start, endDate: end });
        setAnchorEl(null)
    }

    const handleClickDate = date => () => {
        const momentObj = moment(date)

        if (dateStart === null) {
            setDateStart(momentObj)
            setDateEnd(null)
            setTurn(1)
        }
        else {
            if (dateEnd === null) {
                setDateEnd(momentObj)
                setTurn(0)
            }
            else {
                if (turn === 0) {
                    setDateStart(momentObj)
                    setDateEnd(null)
                    setTurn(1)
                }
                else if (turn === 1) {
                    setDateEnd(momentObj)
                    setTurn(0)
                }
            }
        }
    }

    const handleChangeNextDate = type => () => {
        switch (type) {
            case 'CHECK_IN':
                if (dateStart) {
                    const dayAfterDateStart = moment(dateStart).add(1, 'days')
                    if (dayAfterDateStart.isSame(moment(dateEnd))) {
                        setDateStart(dayAfterDateStart)
                        setDateEnd(moment(dateEnd).add(1, 'days'))
                    }
                    else {
                        setDateStart(dayAfterDateStart)
                    }
                }
                break
            case 'CHECK_OUT':
                if (dateEnd) {
                    const dayAfterDateEnd = moment(dateEnd).add(1, 'days')
                    if (max !== null) {
                        if (dayAfterDateEnd.format('DD-MM-YYYY') !== moment(max).add(1, 'days').format('DD-MM-YYYY')) {
                            setDateEnd(dayAfterDateEnd)
                        }
                    }
                    else {
                        setDateEnd(dayAfterDateEnd)
                    }
                }
                break
        }
    }

    const handleChangePreviousDate = type => () => {
        switch (type) {
            case 'CHECK_IN':
                if (dateStart) {
                    const dayBeforeDateStart = moment(dateStart).subtract(1, 'days')
                    if (min !== null) {
                        if (dayBeforeDateStart.format('DD-MM-YYYY') !== moment(min).subtract(1, 'days').format('DD-MM-YYYY')
                        ) {
                            setDateStart(dayBeforeDateStart)
                        }
                    }
                    else {
                        setDateStart(dayBeforeDateStart)
                    }
                }
                break
            case 'CHECK_OUT':
                if (dateEnd) {
                    setDateEnd(moment(dateEnd).subtract(1, 'days'))
                }
                break
        }
    }

    const handleChangeNextMonth = () => {
        setSelectedMonth(moment(selectedMonth).add(1, 'M'))
    }

    const handleChangePreviousMonth = () => {
        setSelectedMonth(moment(selectedMonth).subtract(1, 'M'))
    }

    const handleResetDate = () => {
        setDateStart(moment())
        setDateEnd(moment().add(1, 'days'))
        setTurn(0)
    }

    useEffect(() => {
        const clonedSelectedMonth = moment(selectedMonth).clone()
        const data = []

        let dateObj = null
        let day = null
        let diff = 0

        for (let monthIndex = 0; monthIndex <= 1; monthIndex++) {
            dateObj = clonedSelectedMonth.add(monthIndex, 'M')
            data.push({
                monthLabel: dateObj.format('MMMM'),
                year: dateObj.format('YYYY'),
                month: dateObj.format('MM'),
                daysInMonth: dateObj.daysInMonth(),
                days: []
            })

            for (let date = 0; date < data[monthIndex].daysInMonth; date++) {
                day = moment(`${data[monthIndex].year}-${data[monthIndex].month}-${formatDoubleDigitDate(`${date + 1}`)}`).format('dddd')

                if (date === 0) {
                    diff = calculateDifferenceFromStart(day)
                    if (diff !== 0) {
                        for (let temp = 0; temp < diff; temp++) {
                            data[monthIndex].days.push({
                                date: null,
                                dayName: null,
                                value: null
                            })
                        }
                    }
                }

                data[monthIndex].days.push({
                    date: formatDoubleDigitDate(`${date + 1}`),
                    dayName: day,
                    value: `${data[monthIndex].year}-${data[monthIndex].month}-${formatDoubleDigitDate(`${date + 1}`)}`
                })

                if (date === data[monthIndex].daysInMonth - 1) {
                    diff = calculateDifferenceFromEnd(day)
                    if (diff !== 0) {
                        for (let temp = 0; temp < diff; temp++) {
                            data[monthIndex].days.push({
                                date: null,
                                dayName: null,
                                value: null
                            })
                        }
                    }
                }
            }
        }

        setDatepickerData(data)
    }, [selectedMonth])

    return (
        <div>
            <div style={{ width: '100%', display: 'grid', gridTemplateColumns: 'auto auto', gridGap: 12 }}>
                <Form
                    id="popover-anchor"
                    label={labelStart}
                    placeholder={placeholderStart}
                    value={start}
                    // required
                    // value={email}
                    // onChange={handleChangeEmail}
                    // error={emailError}
                    // errorMessage={emailError && t(`manage.fieldRequired`)}
                    // tabIndex={1}
                    onClick={handleClickDateInput}
                />
                <Form
                    // id="popover-anchor"
                    label={labelEnd}
                    placeholder={placeholderEnd}
                    value={end}
                    // required
                    // value={email}
                    // onChange={handleChangeEmail}
                    // error={emailError}
                    // errorMessage={emailError && t(`manage.fieldRequired`)}
                    // tabIndex={1}
                    onClick={handleClickDateInput}
                />
            </div>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                style={{ marginTop: 6 }}
                slotProps={{
                    paper: {
                        component: CustomPaper
                    }
                }}
            >
                <DesktopDatepicker
                    labelStart={labelStart}
                    labelEnd={labelEnd}
                    datepickerData={datepickerData}
                    onDateClick={handleClickDate}
                    onChangeNextDate={handleChangeNextDate}
                    onChangePreviousDate={handleChangePreviousDate}
                    handleChangeNextMonth={handleChangeNextMonth}
                    handleChangePreviousMonth={handleChangePreviousMonth}
                    dateStart={dateStart}
                    dateEnd={dateEnd}
                    min={min}
                    max={max}
                    onClose={handleSubmitPopover}
                    onReset={handleResetDate}
                />
            </Popover>
        </div>
    )
}

export default CustomDateRangePicker